import { z } from 'zod';
import { errorMessages } from './romanianLabels';
import { isValidCNP } from './cnp';
import {
  PatientSex,
  BirthOutcome,
  CHDSubgroup,
  PrenatalMethod,
  Outcome30Day,
  MajorMinorFlag,
  ProcedureType,
  DataSource,
} from '@/types/patient.types';

const maternalDataSchema = z.object({
  maternal_age: z
    .number()
    .min(12, 'Vârsta maternă minimă este 12 ani')
    .max(60, 'Vârsta maternă maximă este 60 ani')
    .optional()
    .nullable(),
  maternal_conditions_diabetes: z.boolean().optional(),
  maternal_conditions_lupus: z.boolean().optional(),
  maternal_conditions_hta: z.boolean().optional(),
  maternal_conditions_other_active: z.boolean().optional(),
  maternal_conditions_other: z.string().max(500, 'Maximum 500 caractere').optional().nullable(),
  exposures_smoking: z.boolean().optional(),
  exposures_teratogenic_meds: z.boolean().optional(),
  exposures_infection: z.boolean().optional(),
  exposures_other: z.boolean().optional(),
  exposures_other_details: z.string().max(500, 'Maximum 500 caractere').optional().nullable(),
  folate_supplementation: z
    .number()
    .int()
    .min(1, 'Selectați o opțiune validă')
    .max(3, 'Selectați o opțiune validă')
    .optional()
    .nullable(),
}).partial();

// Enhanced Patient validation schema with MCC registry fields
export const patientSchema = z.object({
  // Existing fields
  name: z
    .string()
    .min(2, errorMessages.minLength(2))
    .max(100, errorMessages.maxLength(100))
    .trim(),
  
  cnp: z
    .string()
    .regex(/^\d{13}$/, 'CNP trebuie să conțină exact 13 cifre')
    .refine((value) => isValidCNP(value), 'CNP invalid (cifra de control nu corespunde)')
    .optional()
    .or(z.literal('')),
  
  birth_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, errorMessages.invalidDate)
    .refine((date) => {
      const birthDate = new Date(date);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      return birthDate <= today;
    }, errorMessages.futureDate),
  
  gender: z
    .number()
    .int()
    .min(0)
    .max(1),
  
  birth_weight: z
    .number()
    .min(300, errorMessages.minValue(300))
    .max(6000, errorMessages.maxValue(6000))
    .optional()
    .nullable(),

  gestational_age: z
    .number()
    .min(22, errorMessages.minValue(22))
    .max(43, errorMessages.maxValue(43))
    .optional()
    .nullable(),
  
  hospital_id: z
    .number()
    .positive('Selectați un spital')
    .int('ID spital invalid'),
    
  patient_code: z.string().optional(),
  external_id: z.string().optional(),
  county: z.string().optional(),
  locality: z.string().optional(),
  maternity: z.string().optional(),
  id_judet: z.number().int().positive().nullable().optional(),
  id_localitate: z.number().int().positive().nullable().optional(),
  mother_name: z.string().optional(),
  father_name: z.string().optional(),
  notes: z.string().optional(),
  
  // MCC Registry fields
  case_id: z.string().uuid().optional(),
  centre: z.number().int().positive().optional(),
  sex: z.nativeEnum(PatientSex).optional(),
  ga_weeks: z
    .number()
    .min(22, 'Vârsta gestațională minimă este 22 săptămâni')
    .max(43, 'Vârsta gestațională maximă este 43 săptămâni')
    .optional(),
  bw_grams: z
    .number()
    .min(300, 'Greutatea minimă este 300g')
    .max(6000, 'Greutatea maximă este 6000g')
    .optional(),
  birth_outcome: z.nativeEnum(BirthOutcome).optional(),
  subgroup_chd: z.nativeEnum(CHDSubgroup).optional(),
  icd10_code: z
    .string()
    .regex(/^(Q\d{2}(\.\d{1,2})?(\*\d+)?|COMPLEX\*\d+)$/, 'Cod ICD-10 MCC invalid (ex: Q21.0)')
    .optional()
    .or(z.literal('')),
  severe_chd: z.boolean().optional(),
  mca_flag: z.boolean().optional(),
  syndrome: z.string().max(200, 'Maximum 200 caractere').optional(),
  genetic_condition_icd10_code: z.string().nullable().optional(),
  prenatal_dx: z.boolean().optional(),
  prenatal_method: z.nativeEnum(PrenatalMethod).optional(),
  ga_prenatal_dx: z
    .number()
    .min(10, 'GA diagnostic prenatal minim 10 săptămâni')
    .max(40, 'GA diagnostic prenatal maxim 40 săptămâni')
    .optional(),
  outcome_30d: z.nativeEnum(Outcome30Day).optional(),
  reintervention_1y: z.boolean().optional(),
  procedure_type: z.nativeEnum(ProcedureType).optional(),
  procedure_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, errorMessages.invalidDate)
    .optional()
    .or(z.literal('')),
  major_minor_flag: z.nativeEnum(MajorMinorFlag).optional(),
  data_source: z.nativeEnum(DataSource).optional(),
  data_quality_note: z.string().optional(),
  other_anomalies_icd10: z.string().optional(),
  maternal_data: maternalDataSchema.optional(),
});

export type PatientFormData = z.infer<typeof patientSchema>;

// Procedure validation schema
export const procedureSchema = z.object({
  patient_id: z.number().positive('ID pacient este obligatoriu'),
  procedure_type_id: z.number().positive('Tipul procedurii este obligatoriu'),
  procedure_name: z
    .string()
    .max(200, 'Maximum 200 caractere')
    .optional(),
  procedure_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, errorMessages.invalidDate),
  outcome_30d: z
    .string()
    .max(100, 'Maximum 100 caractere')
    .optional(),
  complications: z.string().optional(),
  reintervention_1y: z
    .string()
    .max(100, 'Maximum 100 caractere')
    .optional(),
  performed_by_user_id: z.number().positive().optional(),
  hospital_id: z.number().positive().optional(),
  notes: z.string().optional(), // Brief admin notes
  note: z.string().optional(),  // Detailed doctor notes
});

export type ProcedureFormData = z.infer<typeof procedureSchema>;


// Denominator data validation schema
export const denominatorDataSchema = z.object({
  year: z
    .number()
    .min(2000, 'Anul minim este 2000')
    .max(2100, 'Anul maxim este 2100'),
  catchment_area: z
    .string()
    .max(200, 'Maximum 200 caractere')
    .optional(),
  total_livebirths: z
    .number()
    .min(0, 'Numărul nu poate fi negativ')
    .optional(),
  total_stillbirths_ge24w: z
    .number()
    .min(0, 'Numărul nu poate fi negativ')
    .optional(),
  hospital_id: z.number().positive().optional(),
  notes: z.string().optional(),
});

export type DenominatorDataFormData = z.infer<typeof denominatorDataSchema>;

// Login validation schema
export const loginSchema = z.object({
  username: z
    .string()
    .min(1, errorMessages.required)
    .trim(),
  
  password: z
    .string()
    .min(1, errorMessages.required),
    
  remember_me: z
    .boolean()
    .optional(),
});

export type LoginFormData = z.infer<typeof loginSchema>;

// Change password validation schema
export const changePasswordSchema = z.object({
  current_password: z
    .string()
    .min(1, errorMessages.required),
  
  new_password: z
    .string()
    .min(8, errorMessages.passwordTooShort),
  
  confirm_password: z
    .string()
    .min(1, errorMessages.required),
}).refine((data) => data.new_password === data.confirm_password, {
  message: errorMessages.passwordMismatch,
  path: ['confirm_password'],
});

export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

// Diagnosis validation schema
export const diagnosisSchema = z.object({
  patient_id: z
    .number()
    .positive(),
  
  icd10_code: z
    .string()
    .regex(
      /^(?:[A-Z]\d{2}(?:\.[0-9X]{1,4})?(?:\*\d+)?|COMPLEX\*\d+)$/,
      'Cod ICD-10 invalid (ex: Q21.0)',
    )
    .trim(),
  
  severity_level: z
    .number()
    .int()
    .min(1, 'Severitatea minimă este 1')
    .max(5, 'Severitatea maximă este 5'),
    
  is_primary: z
    .boolean(),
    
  diagnosis_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, errorMessages.invalidDate),
    
  notes: z
    .string()
    .max(500, 'Maximum 500 caractere')
    .optional(),
});

export type DiagnosisFormData = z.infer<typeof diagnosisSchema>;


// User validation schema
export const userSchema = z.object({
  name: z
    .string()
    .min(2, errorMessages.minLength(2))
    .max(100, errorMessages.maxLength(100))
    .trim(),
  
  email: z
    .string()
    .email(errorMessages.invalidEmail)
    .trim(),
  
  username: z
    .string()
    .min(3, errorMessages.minLength(3))
    .max(50, errorMessages.maxLength(50))
    .trim(),
  
  password: z
    .string()
    .min(8, errorMessages.passwordTooShort)
    .optional(),
  
  user_level: z
    .number()
    .int()
    .min(1)
    .max(3),
  
  hospital_id: z
    .number()
    .positive()
    .optional()
    .nullable(),
    
  is_active: z
    .boolean()
    .default(true),
});

export type UserFormData = z.infer<typeof userSchema>;

// Forgot password validation schema
export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .email(errorMessages.invalidEmail)
    .trim(),
});

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

// Reset password validation schema
export const resetPasswordSchema = z.object({
  token: z
    .string()
    .min(1, 'Token-ul de resetare este obligatoriu'),
  
  new_password: z
    .string()
    .min(8, errorMessages.passwordTooShort)
    .regex(/[A-Z]/, 'Parola trebuie să conțină cel puțin o literă mare')
    .regex(/[a-z]/, 'Parola trebuie să conțină cel puțin o literă mică')
    .regex(/\d/, 'Parola trebuie să conțină cel puțin o cifră')
    .regex(/[^A-Za-z0-9]/, 'Parola trebuie să conțină cel puțin un caracter special'),
  
  confirm_new_password: z
    .string()
    .min(8, errorMessages.passwordTooShort),
}).refine((data) => data.new_password === data.confirm_new_password, {
  message: errorMessages.passwordMismatch,
  path: ['confirm_new_password'],
});

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

// Hospital validation schema
export const hospitalSchema = z.object({
  name: z
    .string()
    .min(2, errorMessages.minLength(2))
    .max(200, errorMessages.maxLength(200))
    .trim(),
  
  code: z
    .string()
    .min(2, errorMessages.minLength(2))
    .max(20, errorMessages.maxLength(20))
    .trim(),
  
  county: z
    .string()
    .min(2, errorMessages.minLength(2))
    .max(50, errorMessages.maxLength(50))
    .trim(),
  
  city: z
    .string()
    .min(2, errorMessages.minLength(2))
    .max(50, errorMessages.maxLength(50))
    .trim(),
  
  address: z
    .string()
    .max(500, errorMessages.maxLength(500))
    .optional(),
  
  phone: z
    .string()
    .regex(/^(\+4|004)?[0-9]{10}$/, 'Număr de telefon invalid')
    .optional()
    .nullable(),
  
  email: z
    .string()
    .email(errorMessages.invalidEmail)
    .optional()
    .nullable(),
    
  is_active: z
    .boolean()
    .default(true),
});

export type HospitalFormData = z.infer<typeof hospitalSchema>;
